import { useState } from 'react';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material';
import { StyledButton } from 'components/Buttons';
import { LoaderFullScreen } from 'components/Loaders/LoaderFullScreen';
import { useTranslation } from 'react-i18next';

type Props = {
  open: boolean;
  onClose: () => void;
  onSubmit: () => Promise<void>;
  isOwn: boolean;
  sendsToKsef: boolean;
  invoiceCode: string;
  language: 'pl' | 'en';
};

export const PublishDialog = ({
  open,
  onClose,
  onSubmit,
  isOwn,
  sendsToKsef,
  invoiceCode,
  language,
}: Props) => {
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation(['misc', 'invoice']);

  const submitLabel = sendsToKsef
    ? t('publishToKsef', { ns: 'invoice', language: language.toUpperCase() })
    : t('button.publish', { ns: 'misc' });

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await onSubmit();
      onClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth='sm'
      fullWidth
      aria-labelledby='publish-invoice-title'
      aria-describedby='publish-invoice-description'
    >
      <LoaderFullScreen loading={loading} />
      <DialogTitle
        id='publish-invoice-title'
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          flexWrap: 'wrap',
          gap: 1.25,
          fontSize: '1.1rem',
          fontWeight: 600,
        }}
      >
        {sendsToKsef
          ? t('publishKsefHeading', { ns: 'invoice' })
          : t('button.publish', { ns: 'misc' })}
        <Typography component='span' variant='body2' color='text.secondary'>
          {invoiceCode}
        </Typography>
      </DialogTitle>
      <DialogContent dividers style={{ paddingTop: 16 }}>
        <DialogContentText id='publish-invoice-description'>
          {sendsToKsef
            ? t('publishKsefSummary', { ns: 'invoice' })
            : t('modal.publishInvoiceBody', { ns: 'misc' })}
        </DialogContentText>
        {sendsToKsef ? (
          <Box component='ol' sx={{ m: 0, mt: 2.5, p: 0, listStyle: 'none' }}>
            {[
              {
                title: t('publishKsefSendTitle', { ns: 'invoice' }),
                description: t('publishKsefSendDescription', {
                  ns: 'invoice',
                }),
              },
              {
                title: t('publishKsefPublishTitle', { ns: 'invoice' }),
                description: t('publishKsefPublishDescription', {
                  ns: 'invoice',
                  language: language.toUpperCase(),
                }),
              },
              {
                title: t('publishKsefEditTitle', { ns: 'invoice' }),
                description: t('publishKsefEditDescription', { ns: 'invoice' }),
              },
            ].map((step, index) => (
              <Box
                component='li'
                key={step.title}
                sx={{
                  display: 'flex',
                  gap: 1.5,
                  '&:not(:last-child)': { mb: 2.5 },
                }}
              >
                <Box
                  component='span'
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    bgcolor: 'action.hover',
                    color: 'text.secondary',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                  }}
                >
                  {index + 1}
                </Box>
                <Box>
                  <Typography variant='body2' fontWeight={700}>
                    {step.title}
                  </Typography>
                  <Typography
                    variant='body2'
                    color='text.secondary'
                    sx={{ mt: 0.5 }}
                  >
                    {step.description}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        ) : isOwn ? (
          <DialogContentText sx={{ mt: 1 }}>
            {t('modal.publishAlert', { ns: 'misc' })}
          </DialogContentText>
        ) : null}
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          gap: 1,
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          '& .MuiButton-root': { m: 0, width: { xs: '100%', sm: 'auto' } },
        }}
      >
        <StyledButton onClick={onClose} disabled={loading}>
          {t('button.cancel', { ns: 'misc' })}
        </StyledButton>
        <StyledButton $primary onClick={handleSubmit} disabled={loading}>
          {submitLabel}
        </StyledButton>
      </DialogActions>
    </Dialog>
  );
};
